import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"
import eventBus from "@/lib/vue/plugins/event_bus"
import { positionVars, oppositeDirection } from "@/lib/tooltip_positioning"

export default class extends Controller {
  static targets = ["tooltip"]

  onFrameLoad = (event) => this.registerTriggersOn(event.target)
  onVueRegister = (event) => this.registerTriggersOn(event.trigger_container)

  connect() {
    this.cache = {}

    document.addEventListener("turbo:frame-load", this.onFrameLoad)
    eventBus.$on("turbo-tooltip:register", this.onVueRegister)

    this.tooltipTarget.addEventListener("mouseenter", () => clearTimeout(this.closeTimer))
    this.tooltipTarget.addEventListener("mouseleave", () => this.scheduleHoverClose())

    this.registerTriggersOn(document)
  }

  disconnect() {
    document.removeEventListener("turbo:frame-load", this.onFrameLoad)
    eventBus.$off("turbo-tooltip:register", this.onVueRegister)
    clearTimeout(this.openTimer)
    clearTimeout(this.closeTimer)
  }

  registerTriggersOn(container) {
    container.querySelectorAll("[role='turbo-tooltip']").forEach((trigger) => {
      // Turbo would otherwise prefetch the trigger links on hover
      trigger.dataset.turboPrefetch = false

      if (trigger.dataset.tooltipTrigger === "hover") this.registerHoverTrigger(trigger)
      else this.registerClickTrigger(trigger)
    })
  }

  registerClickTrigger(trigger) {
    trigger.onclick = (event) => {
      event.preventDefault()
      if (this.togglesClosed(trigger)) this.close()
      else this.show(trigger, { openedByHover: false })
    }
  }

  togglesClosed(trigger) {
    return trigger.dataset.tooltipToggle === "true" && !this.element.hidden && this.openTrigger === trigger
  }

  registerHoverTrigger(trigger) {
    trigger.onmouseenter = () => {
      clearTimeout(this.closeTimer)
      this.openTimer = setTimeout(() => this.show(trigger, { openedByHover: true }), 200)
    }
    trigger.onmouseleave = () => {
      clearTimeout(this.openTimer)
      this.scheduleHoverClose()
    }
    // A tap arrives as a click; where hover exists it already showed the tooltip
    trigger.onclick = (event) => {
      if (window.matchMedia("(hover: hover)").matches) return

      event.preventDefault()
      this.show(trigger, { openedByHover: true })
    }
  }

  scheduleHoverClose() {
    if (!this.openedByHover) return

    this.closeTimer = setTimeout(() => this.close(), 300)
  }

  async show(trigger, { openedByHover }) {
    const href = trigger.dataset.tooltipHref || trigger.getAttribute("href")
    this.requestedHref = href

    if (!this.cache[href]) {
      const response = await fetch(href, { headers: { Accept: "text/vnd.turbo-stream.html" } })
      if (!response.ok) return
      this.cache[href] = await response.text()
    }

    // The user may have moved on to another trigger while we fetched
    if (this.requestedHref !== href) return

    Turbo.renderStreamMessage(this.cache[href])
    this.position(trigger)
    this.openedByHover = openedByHover
    this.openTrigger = trigger
    this.element.hidden = false
  }

  close() {
    this.openTrigger = null
    this.element.hidden = true
  }

  closeOnOutsideClick(event) {
    if (this.element.hidden) return
    if (this.tooltipTarget.contains(event.target)) return
    if (event.target.closest("[role='turbo-tooltip']")) return

    this.close()
  }

  // Direction top anchors by top + md:-translate-y-full: with no positioned
  // ancestor, `bottom` would resolve against the viewport-sized containing block
  position(trigger) {
    const direction = trigger.dataset.tooltipDirection || "right"
    const vars = positionVars({
      direction,
      rect: trigger.getBoundingClientRect(),
      scroll: { x: window.scrollX, y: window.scrollY },
      documentWidth: document.documentElement.scrollWidth,
      viewportWidth: document.documentElement.clientWidth,
      boundaries: this.boundariesFrom(trigger, oppositeDirection(direction)),
    })

    for (const side of ["top", "right", "bottom", "left"]) {
      if (vars[side]) this.tooltipTarget.style.setProperty(`--tooltip-${side}-position`, vars[side])
      else this.tooltipTarget.style.removeProperty(`--tooltip-${side}-position`)
    }

    this.tooltipTarget.classList.toggle("md:-translate-y-full", direction === "top")
  }

  boundariesFrom(element, direction) {
    if (!["left", "right"].includes(direction)) return 0

    const styles = getComputedStyle(element)
    const capitalizedDirection = direction[0].toUpperCase() + direction.slice(1)

    return (
      parseFloat(styles[`margin${capitalizedDirection}`]) +
      parseFloat(styles[`padding${capitalizedDirection}`]) +
      parseFloat(styles[`border${capitalizedDirection}Width`])
    )
  }
}
